import { useState, useEffect } from 'react';
import { themeI18n } from '../utils/i18n_helper';
import { Map, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { getDistance } from '../utils/store-locator';

import { StoreLocatorMarker } from './StoreLocatorMarker';
import { StoreLocation } from './StoreLocation';
import Icons from './Icons.js';
import styling_presets from '../utils/maps_styling_presets.json';

const userLocation = JSON.parse(localStorage.getItem('theme:user_location'));
const sectionSettings = JSON.parse(document.querySelector('[data-store-locations-section-settings]').innerHTML);
const storeLocationsData = JSON.parse(document.querySelector('[data-store-locations-data]').innerHTML);

export const StoreLocatorSection = () => {
  const [storeLocations, setStoreLocations] = useState(storeLocationsData.locations); // consider using GraphQL to fetch store locations if many locations
  const [userAddress, setUserAddress] = useState('');
  const [userLatLng, setUserLatLng] = useState({ lat: userLocation?.lat, lng: userLocation?.lng });
  const [activeTab, setActiveTab] = useState('list');
  const [activeStore, setActiveStore] = useState(null);
  const [apiLoaded, setApiLoaded] = useState(false); // checks if google maps api is loaded

  const map = useMap('store-locator-map'); // gives access to the instanced map object

  const geocodingLibrary = useMapsLibrary('geocoding');
  const coreLibrary = useMapsLibrary('core');

  const geocodeAddress = () => {
    const geocoder = new geocodingLibrary.Geocoder();

    geocoder.geocode({ address: userAddress }, (results, status) => {
      if (status === 'OK') {
        setUserLatLng({
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng(),
        });

        if (window.toast) {
          window.toast.success({
            message: themeI18n('store_locator.address_success')
          });
        }
      } else {
        if (window.toast) {
          window.toast.error({
            message: themeI18n('store_locator.address_error')
          });
        }
      }
    });
  }

  const sortStoresByDistance = () => {
    const updatedStores = storeLocations.map(store => {
      const distance = getDistance(userLatLng.lat, parseFloat(store.latitude), userLatLng.lng,  parseFloat(store.longitude));
      return {
        ...store,
        distance,
      };
    });

    setStoreLocations(updatedStores.sort((a, b) => a.distance - b.distance));
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!document.querySelector('.store-locator__map').contains(event.target) && !document.querySelector('.store-locator__stores-list').contains(event.target)) {
        setActiveStore(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (storeLocations && coreLibrary) {
      const bounds = new coreLibrary.LatLngBounds();
      storeLocations.forEach(store => {
        bounds.extend({lat: parseFloat(store.latitude), lng: parseFloat(store.longitude)});
      });

      // zoom and pan to fit all store location markers
      map?.setCenter(bounds.getCenter());
      map?.fitBounds(bounds);
    }
  }, [storeLocations, apiLoaded, map]);

  // sort stores by distance when user location is updated
  useEffect(() => {
    if (storeLocations && userLatLng.lat && userLatLng.lng) {
      sortStoresByDistance();
      localStorage.setItem('theme:user_location', JSON.stringify(userLatLng));

      if (userAddress) {
        localStorage.setItem('theme:user_location_name', userAddress);
        setUserLocationName(userAddress);
      }
    }
  }, [userLatLng]);

  useEffect(() => {
    const checkApiLoaded = setInterval(() => {
      if (window.google && window.google.maps.Map) {
        setApiLoaded(true);
        clearInterval(checkApiLoaded);
      }
    }, 100);

    return () => clearInterval(checkApiLoaded);
  }, []);

  return (
    <div className="store-locator__content">
      <RenderAddressForm geocodeAddress={geocodeAddress} className={"hide-desktop-up"} userAddress={userAddress} setUserAddress={setUserAddress} />

      <div className='store-locator__tab-buttons hide-desktop-up'>
        <button className='store-locator__tab-button' open={activeTab == 'list'} onClick={() => setActiveTab('list')}>
          {themeI18n('store_locator.list')}
        </button>

        <button className='store-locator__tab-button' open={activeTab == 'map'} onClick={() => setActiveTab('map')}>
          {themeI18n('store_locator.map')}
        </button>
      </div>

      <div className='store-locator__controls'>
          <RenderAddressForm geocodeAddress={geocodeAddress} className={"hide-desktop-down"} userAddress={userAddress} setUserAddress={setUserAddress} />

        <div className='store-locator__stores-list' open={activeTab == 'list'}>
          {storeLocations?.map(store => {
            return (
              <StoreLocation key={store.id} store={store} map={map} setActiveStore={setActiveStore} />
            )
          })}
        </div>
      </div>

        {apiLoaded ? (
        <Map
          className={`store-locator__map ${activeTab == 'map' ? 'open' : ''}`}
          defaultCenter={{lat: 22.54992, lng: 0}}
          defaultZoom={3}
          gestureHandling={'greedy'}
          disableDefaultUI={true}
         
          id='store-locator-map'
        >
          {storeLocations?.map(store => {
            return (
              <StoreLocatorMarker key={store.id} store={store} setActiveStore={setActiveStore} activeStore={activeStore} />
            )
          })}

          <div className='store-locator__active-store hide-desktop-up'>
            {activeStore && (
              <StoreLocation store={activeStore} map={map} setActiveStore={setActiveStore} />
            )}
          </div>
        </Map>
      ) : (
        <div className='store-locator__map loading'>
        </div>
      )}
    </div>
  )
}

const RenderAddressForm = ({ className, userAddress, setUserAddress, geocodeAddress }) => (
  <form className={`store-locator__form ${className}`} onSubmit={(event)=> {
    event.preventDefault();

    geocodeAddress(event);
  }}>
    <Icons icon='search' size='small' className='store-locator__search-icon' />

    <input type="text" id="postal-code" value={userAddress} onChange={(event) => setUserAddress(event.target.value)} required placeholder={themeI18n('store_locator.address_placeholder')}/>

    <button className='store-locator__search-submit' type="submit" aria-label={themeI18n('store_locator.address_submit')}>
      <Icons icon='submit' size='small' />
    </button>
  </form>
)
